import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { Loader2, Trash2, PlusCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useNotification } from '@/components/notifications';
import HelpTooltip from '@/components/ui/help-tooltip';
import { TagInput } from '@/components/ui/tag-input';
import { updateCollection } from '@/services/data-sources/update-collection';
import type {
  WordPressContentRuleMode,
  WordPressExclusionRule,
  WordPressFieldMatch,
} from '@/types/source-types';

interface WordPressExclusionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId: string;
  initialRules: WordPressExclusionRule[];
  existingSourceConfig?: Record<string, unknown>;
  onRulesSaved: () => void;
  // Per-site context (optional -- when provided, modal operates on a specific site)
  siteId?: string;
  siteLabel?: string;
  onSiteSave?: (siteId: string, rules: WordPressExclusionRule[]) => void;
}

interface RuleFormErrors {
  name?: string;
  reason?: string;
  conditions?: string;
}

interface RuleWithErrors extends WordPressExclusionRule {
  errors?: RuleFormErrors;
}

const createEmptyRule = (): WordPressExclusionRule => ({
  name: '',
  mode: 'exclude',
  title_contains: null,
  tags: null,
  categories: null,
  field_matches: null,
  reason: '',
});

// Drop rows missing either side; both key and value are required for a match.
const cleanFieldMatches = (
  fieldMatches: WordPressFieldMatch[] | null | undefined,
): WordPressFieldMatch[] =>
  (fieldMatches ?? [])
    .map((fm) => ({ key: fm.key.trim(), value: fm.value.trim() }))
    .filter((fm) => fm.key.length > 0 && fm.value.length > 0);

const normalizeRuleForComparison = (
  rule: WordPressExclusionRule,
): WordPressExclusionRule => ({
  name: rule.name.trim(),
  // Legacy rows may omit mode; treat missing as 'exclude' so an unchanged
  // legacy rule doesn't falsely register as modified.
  mode: rule.mode ?? 'exclude',
  reason: (rule.reason ?? '').trim(),
  title_contains:
    rule.title_contains && rule.title_contains.length > 0
      ? [...rule.title_contains].sort()
      : null,
  tags: rule.tags && rule.tags.length > 0 ? [...rule.tags].sort() : null,
  categories:
    rule.categories && rule.categories.length > 0
      ? [...rule.categories].sort()
      : null,
  field_matches: (() => {
    const cleaned = cleanFieldMatches(rule.field_matches);
    return cleaned.length > 0
      ? [...cleaned].sort(
          (a, b) =>
            a.key.localeCompare(b.key) || a.value.localeCompare(b.value),
        )
      : null;
  })(),
});

const rulesAreEqual = (
  rules1: WordPressExclusionRule[],
  rules2: WordPressExclusionRule[],
): boolean => {
  if (rules1.length !== rules2.length) return false;
  const normalized1 = rules1.map(normalizeRuleForComparison);
  const normalized2 = rules2.map(normalizeRuleForComparison);
  return JSON.stringify(normalized1) === JSON.stringify(normalized2);
};

const validateRule = (rule: WordPressExclusionRule): RuleFormErrors => {
  const errors: RuleFormErrors = {};

  if (!rule.name.trim()) {
    errors.name = 'Rule name is required';
  } else if (rule.name.length > 100) {
    errors.name = 'Rule name must be 100 characters or less';
  }

  if ((rule.reason ?? '').length > 500) {
    errors.reason = 'Reason must be 500 characters or less';
  }

  const hasTitlePatterns =
    Array.isArray(rule.title_contains) && rule.title_contains.length > 0;
  const hasTags = Array.isArray(rule.tags) && rule.tags.length > 0;
  const hasCategories =
    Array.isArray(rule.categories) && rule.categories.length > 0;
  const hasFieldMatches = cleanFieldMatches(rule.field_matches).length > 0;

  if (!hasTitlePatterns && !hasTags && !hasCategories && !hasFieldMatches) {
    errors.conditions =
      'At least one condition required (title patterns, tags, categories, or field matches)';
  }

  return errors;
};

export const WordPressExclusionModal: FC<WordPressExclusionModalProps> = ({
  open,
  onOpenChange,
  collectionId,
  initialRules,
  existingSourceConfig = {},
  onRulesSaved,
  siteId,
  siteLabel,
  onSiteSave,
}) => {
  const notification = useNotification();
  const [rules, setRules] = useState<RuleWithErrors[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  // Determine if operating in per-site mode
  const isPerSiteMode = Boolean(siteId && onSiteSave);

  // Build title with site context
  const modalTitle =
    isPerSiteMode && siteLabel
      ? `WordPress Content Filters - ${siteLabel}`
      : 'WordPress Content Filters';

  // Initialize rules when modal opens
  useEffect(() => {
    if (open) {
      setRules(
        initialRules.length > 0 ? initialRules.map((r) => ({ ...r })) : [],
      );
      setGeneralError(null);
      setShowValidation(false);
    }
  }, [open, initialRules]);

  const handleAddRule = useCallback(() => {
    setRules((prev) => [...prev, createEmptyRule()]);
  }, []);

  const handleDeleteRule = useCallback((index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleRuleChange = useCallback(
    (index: number, field: keyof WordPressExclusionRule, value: unknown) => {
      setRules((prev) =>
        prev.map((rule, i) => {
          if (i !== index) return rule;
          return {
            ...rule,
            [field]: value,
            errors: undefined, // Clear errors when user makes changes
          };
        }),
      );
      // Clear validation display when user makes changes
      setShowValidation(false);
    },
    [],
  );

  const handleAddFieldMatch = useCallback((ruleIndex: number) => {
    setRules((prev) =>
      prev.map((rule, i) =>
        i === ruleIndex
          ? {
              ...rule,
              field_matches: [
                ...(rule.field_matches ?? []),
                { key: '', value: '' },
              ],
              errors: undefined,
            }
          : rule,
      ),
    );
    setShowValidation(false);
  }, []);

  const handleFieldMatchChange = useCallback(
    (
      ruleIndex: number,
      pairIndex: number,
      field: keyof WordPressFieldMatch,
      value: string,
    ) => {
      setRules((prev) =>
        prev.map((rule, i) => {
          if (i !== ruleIndex) return rule;
          const next = [...(rule.field_matches ?? [])];
          next[pairIndex] = { ...next[pairIndex], [field]: value };
          return { ...rule, field_matches: next, errors: undefined };
        }),
      );
      setShowValidation(false);
    },
    [],
  );

  const handleRemoveFieldMatch = useCallback(
    (ruleIndex: number, pairIndex: number) => {
      setRules((prev) =>
        prev.map((rule, i) => {
          if (i !== ruleIndex) return rule;
          const next = (rule.field_matches ?? []).filter(
            (_, p) => p !== pairIndex,
          );
          return {
            ...rule,
            field_matches: next.length > 0 ? next : null,
            errors: undefined,
          };
        }),
      );
      setShowValidation(false);
    },
    [],
  );

  const hasChanges = useMemo(
    () => !rulesAreEqual(rules, initialRules),
    [rules, initialRules],
  );

  const handleSave = useCallback(async () => {
    // No changes to save, just close the modal
    if (!hasChanges) {
      onOpenChange(false);
      return;
    }

    // Always validate all rules
    const validatedRules = rules.map((rule) => ({
      ...rule,
      errors: validateRule(rule),
    }));

    const hasErrors = validatedRules.some(
      (rule) => rule.errors && Object.keys(rule.errors).length > 0,
    );

    // Show validation errors if form is invalid
    if (hasErrors || rules.length === 0) {
      setRules(validatedRules);
      setShowValidation(true);
      return;
    }

    // Prepare rules for API (remove null arrays, keep only non-empty arrays)
    const cleanRules: WordPressExclusionRule[] = rules.map((rule) => ({
      name: rule.name.trim(),
      mode: rule.mode ?? 'exclude',
      reason: (rule.reason ?? '').trim(),
      title_contains:
        rule.title_contains && rule.title_contains.length > 0
          ? rule.title_contains
          : null,
      tags: rule.tags && rule.tags.length > 0 ? rule.tags : null,
      categories:
        rule.categories && rule.categories.length > 0 ? rule.categories : null,
      field_matches: (() => {
        const cleaned = cleanFieldMatches(rule.field_matches);
        return cleaned.length > 0 ? cleaned : null;
      })(),
    }));

    // Per-site mode: delegate save to parent via callback
    if (isPerSiteMode && siteId && onSiteSave) {
      onSiteSave(siteId, cleanRules);
      onOpenChange(false);
      return;
    }

    // Legacy mode: save directly to the API
    setIsSaving(true);
    setGeneralError(null);

    try {
      // Merge with existing source_config to preserve other fields
      const updatedSourceConfig = {
        ...existingSourceConfig,
        wordpress_exclusion_rules: cleanRules.length > 0 ? cleanRules : null,
      };

      const result = await updateCollection({
        id: collectionId,
        sourceConfig: updatedSourceConfig,
      });

      if (result?.detail) {
        // Handle Pydantic validation errors (array of error objects) or string error messages
        let errorMessage: string;
        if (Array.isArray(result.detail)) {
          errorMessage = result.detail
            .map((err: { loc?: string[]; msg?: string }) => {
              const field = err.loc?.join('.') || 'unknown field';
              return `${field}: ${err.msg || 'validation error'}`;
            })
            .join('; ');
        } else {
          errorMessage = String(result.detail);
        }
        setGeneralError(errorMessage);
        notification.error(errorMessage);
        return;
      }

      notification.success('Content filters saved');
      onRulesSaved();
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save content filters';
      setGeneralError(message);
      notification.error(message);
    } finally {
      setIsSaving(false);
    }
  }, [
    hasChanges,
    rules,
    isPerSiteMode,
    siteId,
    onSiteSave,
    existingSourceConfig,
    collectionId,
    notification,
    onRulesSaved,
    onOpenChange,
  ]);

  // Check if all rules are valid (have required fields filled)
  const allRulesValid = useMemo(
    () =>
      rules.every((rule) => {
        const hasName = rule.name.trim().length > 0;
        const hasTitlePatterns =
          Array.isArray(rule.title_contains) && rule.title_contains.length > 0;
        const hasTags = Array.isArray(rule.tags) && rule.tags.length > 0;
        const hasCategories =
          Array.isArray(rule.categories) && rule.categories.length > 0;
        const hasFieldMatches =
          cleanFieldMatches(rule.field_matches).length > 0;
        const hasAtLeastOneCondition =
          hasTitlePatterns || hasTags || hasCategories || hasFieldMatches;

        return hasName && hasAtLeastOneCondition;
      }),
    [rules],
  );

  const canAddRule = useMemo(
    () => rules.length === 0 || allRulesValid,
    [rules.length, allRulesValid],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[90vh] flex flex-col'>
        <DialogHeader>
          <DialogTitle>{modalTitle}</DialogTitle>
          <DialogDescription>
            Define rules to filter WordPress content during ingestion. Each rule
            is either an <strong>Include</strong> (allow-list) or{' '}
            <strong>Exclude</strong>. If any Include rules exist, posts must
            match at least one Include rule to be ingested; Exclude rules apply
            on top. Within a rule, conditions use OR logic.
          </DialogDescription>
        </DialogHeader>

        {generalError && (
          <div className='rounded-lg bg-destructive/10 p-3 text-sm text-destructive'>
            {generalError}
          </div>
        )}

        <div className='flex-1 overflow-y-auto space-y-4 -mx-4 px-4'>
          {rules.length === 0 ? (
            <div className='text-center py-8 space-y-3'>
              <p className='text-muted-foreground'>
                No content filters configured
              </p>
              <p className='text-sm text-muted-foreground'>
                Create rules to include only specific content (allow-list) or
                exclude content from ingestion
              </p>
              <Button onClick={handleAddRule} variant='outline'>
                Add your first rule
              </Button>
            </div>
          ) : (
            rules.map((rule, index) => {
              const ruleMode: WordPressContentRuleMode = rule.mode ?? 'exclude';
              return (
                <Card key={index} className='px-4 py-6 relative gap-4'>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => handleDeleteRule(index)}
                    aria-label='Delete rule'
                    className='absolute top-1 right-1'
                  >
                    <Trash2 className='size-4' />
                  </Button>
                  <div>
                    <div className='flex items-center gap-2 mb-2'>
                      <Label>Mode</Label>
                      <HelpTooltip label='Rule mode help'>
                        <strong>Include</strong>: allow-list. If any Include
                        rule exists, posts must match at least one Include rule
                        to be ingested. <strong>Exclude</strong>: drops matching
                        posts. Exclude rules apply on top of the allow-list.
                      </HelpTooltip>
                    </div>
                    <ToggleGroup
                      type='single'
                      value={ruleMode}
                      onValueChange={(value) => {
                        if (value === 'include' || value === 'exclude') {
                          handleRuleChange(index, 'mode', value);
                        }
                      }}
                      className='justify-start'
                      disabled={isSaving}
                      aria-label='Rule mode'
                    >
                      <ToggleGroupItem
                        value='include'
                        variant='outline'
                        className='px-6'
                      >
                        Include
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value='exclude'
                        variant='outline'
                        className='px-6'
                      >
                        Exclude
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                  <div>
                    <div className='flex items-center gap-2 mb-2'>
                      <Label
                        className='label-required'
                        htmlFor={`rule-${index}-name`}
                      >
                        Rule name
                      </Label>
                      <HelpTooltip label='Rule name help'>
                        A human-readable identifier for this content filter rule
                        (e.g., 'Quiz Content', 'Intelligent Design Only')
                      </HelpTooltip>
                    </div>
                    <Input
                      id={`rule-${index}-name`}
                      value={rule.name}
                      onChange={(e) =>
                        handleRuleChange(index, 'name', e.target.value)
                      }
                      placeholder='e.g., Quiz Content'
                      disabled={isSaving}
                      className={
                        showValidation && !rule.name.trim()
                          ? 'border-destructive'
                          : ''
                      }
                    />
                    {(rule.errors?.name ||
                      (showValidation && !rule.name.trim())) && (
                      <p className='text-sm text-destructive'>
                        {rule.errors?.name || 'Rule name is required'}
                      </p>
                    )}
                  </div>

                  {/* Reason field */}
                  <div>
                    <div className='flex items-center gap-2 mb-2'>
                      <Label htmlFor={`rule-${index}-reason`}>
                        Reason{' '}
                        <span className='text-muted-foreground text-xs font-normal'>
                          (optional)
                        </span>
                      </Label>
                      <HelpTooltip label='Reason help'>
                        Optional note explaining why this rule applies. Helps
                        document the purpose of the rule for future reference.
                      </HelpTooltip>
                    </div>
                    <Textarea
                      id={`rule-${index}-reason`}
                      value={rule.reason ?? ''}
                      onChange={(e) =>
                        handleRuleChange(index, 'reason', e.target.value)
                      }
                      placeholder='e.g., Quizzes contain intentionally false information that reduces RAG quality'
                      rows={2}
                      disabled={isSaving}
                    />
                    {rule.errors?.reason && (
                      <p className='text-sm text-destructive'>
                        {rule.errors.reason}
                      </p>
                    )}
                  </div>

                  {/* Conditions section - at least one required */}
                  <div
                    className={`space-y-3 p-4 rounded-lg border border-dashed ${
                      showValidation &&
                      !rule.title_contains?.length &&
                      !rule.tags?.length &&
                      !rule.categories?.length &&
                      cleanFieldMatches(rule.field_matches).length === 0
                        ? 'border-destructive bg-destructive/5'
                        : 'border-muted-foreground/30 bg-muted/20'
                    }`}
                  >
                    <div className='flex items-center gap-2'>
                      <Label className='font-semibold'>
                        Filter Conditions
                        <span className='text-destructive ml-1'>*</span>
                      </Label>
                      <HelpTooltip label='Filter conditions help'>
                        At least one condition type is required. A post matches
                        this rule if it satisfies ANY of the conditions you
                        specify below.
                      </HelpTooltip>
                    </div>
                    <p className='text-sm text-muted-foreground'>
                      Posts matching any condition will be{' '}
                      <strong>
                        {ruleMode === 'include' ? 'included' : 'excluded'}
                      </strong>
                      . Add at least one condition (title patterns, tags,
                      categories, or field matches).
                    </p>

                    {/* Title patterns */}
                    <div>
                      <div className='flex items-center gap-2 mb-2'>
                        <Label>Title patterns</Label>
                        <HelpTooltip label='Title patterns help'>
                          Posts with titles containing any of these patterns
                          match this rule. Matching is case-insensitive and
                          checks for substring presence (e.g., 'Quiz' matches
                          'Daily Quiz' and 'quiz results').
                        </HelpTooltip>
                      </div>
                      <TagInput
                        value={rule.title_contains || []}
                        onChange={(value) =>
                          handleRuleChange(index, 'title_contains', value)
                        }
                        placeholder='Type pattern and press Enter...'
                        disabled={isSaving}
                      />
                    </div>

                    {/* Tags */}
                    <div>
                      <div className='flex items-center gap-2 mb-2'>
                        <Label>Tags</Label>
                        <HelpTooltip label='Tags help'>
                          Posts with any of these WordPress tags match this
                          rule. Enter the exact tag names as they appear in
                          WordPress (case-insensitive matching).
                        </HelpTooltip>
                      </div>
                      <TagInput
                        value={rule.tags || []}
                        onChange={(value) =>
                          handleRuleChange(index, 'tags', value)
                        }
                        placeholder='Type tag name and press Enter...'
                        disabled={isSaving}
                      />
                    </div>

                    {/* Categories */}
                    <div>
                      <div className='flex items-center gap-2 mb-2'>
                        <Label>Categories</Label>
                        <HelpTooltip label='Categories help'>
                          Posts with any of these WordPress categories match
                          this rule. Enter the exact category names as they
                          appear in WordPress (case-insensitive matching).
                        </HelpTooltip>
                      </div>
                      <TagInput
                        value={rule.categories || []}
                        onChange={(value) =>
                          handleRuleChange(index, 'categories', value)
                        }
                        placeholder='Type category name and press Enter...'
                        disabled={isSaving}
                      />
                    </div>

                    {/* Field matches (ad-hoc top-level key/value pairs) */}
                    <div>
                      <div className='flex items-center gap-2 mb-2'>
                        <Label>Field matches</Label>
                        <HelpTooltip label='Field matches help'>
                          Match on ad-hoc top-level attributes of a post or user
                          (e.g. a custom <code>ingest_in_capitol_ai</code>{' '}
                          flag). The pair matches when the attribute stringifies
                          (case-insensitive) to the value. Add multiple pairs
                          for OR logic.
                        </HelpTooltip>
                      </div>
                      <div className='space-y-2'>
                        {(rule.field_matches ?? []).map((fm, pairIndex) => (
                          <div
                            key={pairIndex}
                            className='flex items-center gap-2'
                          >
                            <Input
                              value={fm.key}
                              onChange={(e) =>
                                handleFieldMatchChange(
                                  index,
                                  pairIndex,
                                  'key',
                                  e.target.value,
                                )
                              }
                              placeholder='Attribute (e.g. ingest_in_capitol_ai)'
                              disabled={isSaving}
                              aria-label='Field match key'
                            />
                            <span className='text-muted-foreground'>=</span>
                            <Input
                              value={fm.value}
                              onChange={(e) =>
                                handleFieldMatchChange(
                                  index,
                                  pairIndex,
                                  'value',
                                  e.target.value,
                                )
                              }
                              placeholder='Value (e.g. true)'
                              disabled={isSaving}
                              aria-label='Field match value'
                            />
                            <Button
                              variant='ghost'
                              size='icon'
                              onClick={() =>
                                handleRemoveFieldMatch(index, pairIndex)
                              }
                              disabled={isSaving}
                              aria-label='Remove field match'
                            >
                              <Trash2 className='size-4' />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => handleAddFieldMatch(index)}
                          disabled={isSaving}
                        >
                          <PlusCircle className='size-4' />
                          Add another
                        </Button>
                      </div>
                    </div>

                    {/* At least one condition required warning */}
                    {(rule.errors?.conditions ||
                      (showValidation &&
                        !rule.title_contains?.length &&
                        !rule.tags?.length &&
                        !rule.categories?.length &&
                        cleanFieldMatches(rule.field_matches).length ===
                          0)) && (
                      <p className='text-sm text-destructive font-medium'>
                        {rule.errors?.conditions ||
                          'At least one condition required (title patterns, tags, categories, or field matches)'}
                      </p>
                    )}
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className='flex justify-between items-center pt-4 border-t'>
          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              onClick={handleAddRule}
              disabled={isSaving || !canAddRule}
            >
              <PlusCircle className='size-4' />
              Add Rule
            </Button>
            {showValidation && !canAddRule && rules.length > 0 && (
              <p className='text-sm text-muted-foreground'>
                Complete the current rule first
              </p>
            )}
          </div>

          <div className='flex gap-2'>
            <Button
              variant='ghost'
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className='size-4 animate-spin' />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
